import Qs from "qs";
import { parseUrlBoolean } from "utilities/parse-url-boolean";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { useQueryParameters } from "@edgetier/client-components";

type IFilters = ReturnType<typeof useSentimentParameters>;

/**
 * Get the existing filters from the URL.
 * @returns Sentiment query parameter values.
 */
export const useSentimentParameters = () => {
    const transforms = { hasFrustration: parseUrlBoolean, hasGratitude: parseUrlBoolean, hasPraise: parseUrlBoolean };
    return useQueryParameters(transforms, { prefix: INTERACTIONS_TABLE_PREFIX });
};

/**
 * Update the URL with the selected sentiment.
 * @param currentFilters Existing query parameters.
 * @param filter         Name of the sentiment parameter being updated.
 * @param search         Existing query string.
 * @returns              New query string value.
 */
export const updateUrl = (currentFilters: IFilters, filter: keyof typeof currentFilters, search: string) => {
    const defaults = { hasFrustration: null, hasGratitude: null, hasPraise: null };
    const filters = currentFilters[filter] ? defaults : { ...defaults, [filter]: true };
    const current = Qs.parse(search, { ignoreQueryPrefix: true });
    return Qs.stringify({ ...current, ...prefixQueryParameters(filters) }, { addQueryPrefix: true, skipNulls: true });
};

/**
 * Prefix a query parameter's name with a string.
 * @param key Unprefixed query parameter name.
 * @returns   Prefixed name.
 */
const prefix = (key: string) => `${INTERACTIONS_TABLE_PREFIX}.${key}`;

/**
 * Prefix all query parameters with a string.
 * @param filters Parameters to prefix.
 * @returns       Same data with new names.
 */
const prefixQueryParameters = (filters: { [name: string]: any }) => {
    return Object.entries(filters).reduce((parameters, [key, value]) => ({ ...parameters, [prefix(key)]: value }), {});
};
